<template>
  <div
    ref="wrapperRef"
    :style="wrapperStyle"
    class="rich-text-editor-wrapper"
    data-testid="rich-text-editor"
  >
    <div
      id="editor"
      ref="editorRef"
    />
  </div>
</template>

<script setup>
import 'quill/dist/quill.snow.css';
import { marked } from 'marked';
import Quill from 'quill';
import QuillResizeImage from 'quill-resize-image';

import { sanitizeRichHtml } from '@/shared/utils/sanitizeRichHtml';

const $bct = inject('translator');

const Inline = Quill.import('blots/inline');
let resizeObserver = null;

// Monkeypatch Quill's default strikethrough format since the default implementation
// uses the <s> tag, which requires a corresponding CSS property
class Strikethrough extends Inline {
  static blotName = 'strike';
  static tagName = 'del';
}

Quill.register('modules/resize', QuillResizeImage);
Quill.register(Strikethrough, true);

const props = defineProps({
  initialText: {
    type: String,
    default: null,
  },
  height: {
    type: [String, Number],
    default: 300,
  },
  resizable: {
    type: Boolean,
    default: true,
  },
  minHeight: {
    type: [String, Number],
    default: 160,
  },
  maxHeight: {
    type: [String, Number],
    default: '95vh',
  },
  /** html = as-is
   *  markdown = run through marked
   *  plaintext = normalize (escape + \\n → br) unless it already looks like HTML
   */
  initialTextFormat: {
    type: String,
    default: 'html',
    validator: (value) => ['html', 'markdown', 'plaintext'].includes(value),
  },
  placeholder: {
    type: String,
    default: null,
  },
  theme: {
    type: String,
    default: 'snow',
  },
  toolbar: {
    type: Array,
    default: () => [
      [{ font: [] }, { header: [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike', { color: [] }, { background: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }, 'blockquote', 'code-block', 'image', 'link'],
      [{ align: [] }, { indent: '+1' }, { indent: '-1' }],
    ],
  },
  toolbarExpanded: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['change:text', 'change:selection']);

defineExpose({
  getSemanticHTML,
  getText,
  insertHtml,
  resetText,
  setText,
});

const editorRef = ref(null);
const wrapperRef = ref(null);
const quill = shallowRef(null);
const displayHeightPx = ref(initialHeightPx());

watch(
  () => props.height,
  () => {
    displayHeightPx.value = initialHeightPx();
  },
);

const fullToolbar = computed(() => {
  return [...props.toolbar, ...props.toolbarExpanded];
});

const maxHeightCss = computed(() => {
  return typeof props.maxHeight === 'number' ? `${props.maxHeight}px` : String(props.maxHeight);
});

const minHeightCss = computed(() => {
  return typeof props.minHeight === 'number' ? `${props.minHeight}px` : String(props.minHeight);
});

const wrapperStyle = computed(() => {
  const base = {
    boxSizing: 'border-box',
    width: '100%',
  };

  if (!props.resizable) {
    return {
      ...base,
      height: `${initialHeightPx()}px`,
    };
  }

  return {
    ...base,
    height: `${displayHeightPx.value}px`,
    maxHeight: maxHeightCss.value,
    minHeight: minHeightCss.value,
    overflow: 'hidden',
    resize: 'vertical',
  };
});

function attachResizeObserver() {
  if (!props.resizable || typeof ResizeObserver === 'undefined' || !wrapperRef.value) return;

  resizeObserver = new ResizeObserver((entries) => {
    const entry = entries[0];
    const measuredHeight = Math.round(entry.contentRect.height);
    const min = parseHeightPx(props.minHeight, 160);
    const max = parseHeightPx(
      props.maxHeight,
      Math.round((typeof window !== 'undefined' ? window.innerHeight : 800) * 0.9),
    );

    if (!Number.isFinite(measuredHeight) || measuredHeight < min) return;

    const clamped = Math.min(max, Math.max(min, measuredHeight));
    if (Math.abs(clamped - displayHeightPx.value) > 1) {
      displayHeightPx.value = clamped;
    }
  });

  resizeObserver.observe(wrapperRef.value);
}

function escapeHtmlPlainTextFragment(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function getSemanticHTML() {
  return sanitizeRichHtml(quill.value?.getSemanticHTML());
}

function getText() {
  return quill.value?.getContents() || '';
}

function initialHeightPx() {
  return parseHeightPx(props.height, 300);
}

function insertHtml(html) {
  if (!quill.value || html == null || html === '') return;

  const safe = sanitizeRichHtml(html);
  const range = quill.value.getSelection(false);
  const index = range ? range.index : Math.max(0, quill.value.getLength() - 1);

  quill.value.clipboard.dangerouslyPasteHTML(index, safe);
}

async function load() {
  await nextTick();

  if (!editorRef.value) return;

  quill.value = new Quill(editorRef.value, {
    modules: {
      toolbar: fullToolbar.value,
      resize: {
        locale: {},
      },
    },
    placeholder: $bct(props.placeholder),
    theme: props.theme,
  });

  resetText();

  quill.value.on('text-change', textChange);
  quill.value.on('selection-change', selectionChange);

  await nextTick();
  attachResizeObserver();
}

function looksLikeRichHtmlStoredCopy(value) {
  const trimmed = String(value).trim();
  return /^<\s*(?:![\w-]|[a-z]|\/\s*[a-z])/i.test(trimmed);
}

/**
 * initialTextFormat: 'plaintext'
 * Stored copy is not treated as HTML unless it already looks like HTML after trim
 * Otherwise escape `&<>\"` then replace `\n` with `<br>` and wrap in `<p>`
 */
function normalizeStoredCopyForQuill(content) {
  if (content == null) return '';

  let text = String(content);
  if (text.trim() === '') return '';

  if (looksLikeRichHtmlStoredCopy(text)) return text;

  if (!text.includes('\n') && /\\n/.test(text)) {
    text = text.replace(/\\n/g, '\n');
  }

  const withUnixBreaks = text.replace(/\r\n/g, '\n');
  const innerHtml = escapeHtmlPlainTextFragment(withUnixBreaks).replaceAll('\n', '<br>');
  if (innerHtml.replaceAll('<br>', '').trim() === '') return '';

  return `<p>${innerHtml}</p>`;
}

function parseHeightPx(value, fallback) {
  if (value == null || value === '') return fallback;
  if (typeof value === 'number' && Number.isFinite(value)) return value;

  const raw = String(value).trim();
  const vhMatch = raw.match(/^([\d.]+)vh$/i);
  if (vhMatch && typeof window !== 'undefined') {
    return Math.round((parseFloat(vhMatch[1]) / 100) * window.innerHeight);
  }

  const pxMatch = raw.match(/^(\d+)px$/i);
  if (pxMatch) return parseInt(pxMatch[1], 10);

  const parsedInt = parseInt(raw, 10);
  return Number.isFinite(parsedInt) ? parsedInt : fallback;
}

function resetText() {
  if (!quill.value) return;

  if (props.initialText == null) {
    return;
  }

  setText(props.initialText, props.initialTextFormat);
}

function selectionChange(_range, _oldRange, source) {
  emit('change:selection', source);
}

function setText(text, format = 'html') {
  if (!quill.value) return;

  let html;

  switch (format) {
    case 'markdown':
      html = marked(text, { renderer: new marked.Renderer() });
      break;
    case 'plaintext':
      html = normalizeStoredCopyForQuill(text ?? '');
      break;
    case 'html':
    default:
      html = text ?? '';
      break;
  }

  if (html === '') {
    quill.value.setText('');
    return;
  }

  quill.value.setContents(quill.value.clipboard.convert({ html }));
}

function textChange(_delta, _oldDelta, source) {
  emit('change:text', source);
}

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;

  if (!quill.value) return;

  quill.value.off('text-change');
  quill.value.off('selection-change');
  quill.value = null;
});

onMounted(load);
</script>

<style>
#editor-resizer > .toolbar {
  display: none !important;
}

/*
 * Quill inserts .ql-toolbar as a sibling before .ql-container. Default .ql-container { height: 100% }
 * is relative to the wrapper, so toolbar height + 100% container exceeds the wrapper and overlaps
 * content below. Flex fixes the editor to the remaining space under the toolbar.
 */
.rich-text-editor-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.rich-text-editor-wrapper > .ql-toolbar {
  flex-shrink: 0;
}

.rich-text-editor-wrapper > .ql-container {
  border: none !important;
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  height: auto !important;
  min-height: 0;
}

.rich-text-editor-wrapper > .ql-container > .ql-editor {
  border: 1px solid #ccc;
  border-top: none;
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
}

.rich-text-editor-wrapper > .ql-container > .ql-editor:focus {
  outline-offset: -1px !important;
}
</style>
